import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

// `error` is a failure nobody has dealt with yet; `resolved` one that is
// linked to a resolved incident or was retried successfully. Both were
// failures — the second is drawn muted so red means "still needs a look".
// Values live in styles.css so each theme can tune them; d3 applies them via
// `.style()` because `var()` is not reliable inside SVG presentation attributes.
export const STATUS_COLORS = { done: 'var(--chart-done)', error: 'var(--chart-error)', resolved: 'var(--chart-resolved)', rendering: 'var(--chart-rendering)' };
const STATUS_LABELS = { done: 'Rendered', error: 'Failed', resolved: 'Failed · resolved', rendering: 'In progress' };

/**
 * Width of the element the chart lives in, tracked live so charts re-lay
 * out when the sidebar collapses or the window resizes. Charts draw only
 * once a real width is known — d3 on a 0px SVG produces NaN paths.
 */
const useWidth = (ref) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return width;
};

const Tooltip = ({ tip }) => tip
  ? <div className="chart-tip" style={{ left: tip.x, top: tip.y }}>{tip.body}</div>
  : null;

const Legend = ({ keys }) => (
  <div className="chart-legend">
    {keys.map((key) => <span key={key}><i style={{ background: STATUS_COLORS[key] }} />{STATUS_LABELS[key]}</span>)}
  </div>
);

/** Renders per day, one bar per day stacked by outcome. */
export function RendersByDay({ daily }) {
  const wrap = useRef(null); const svgRef = useRef(null);
  const width = useWidth(wrap);
  const [tip, setTip] = useState(null);
  const keys = ['done', 'rendering', 'error', 'resolved'];
  const height = 260;

  useEffect(() => {
    if (!width || !daily?.length) return;
    const margin = { top: 12, right: 8, bottom: 34, left: 36 };
    const svg = d3.select(svgRef.current).attr('viewBox', [0, 0, width, height]);
    svg.selectAll('*').remove();

    const x = d3.scaleBand().domain(daily.map((d) => d.day)).range([margin.left, width - margin.right]).padding(daily.length > 40 ? 0.15 : 0.3);
    const yMax = d3.max(daily, (d) => d.done + d.error + d.resolved + d.rendering) || 1;
    const y = d3.scaleLinear().domain([0, yMax]).nice().range([height - margin.bottom, margin.top]);
    const series = d3.stack().keys(keys)(daily);

    svg.append('g').attr('class', 'grid')
      .call(d3.axisLeft(y).ticks(Math.min(5, yMax)).tickSize(-(width - margin.left - margin.right)).tickFormat(''))
      .attr('transform', `translate(${margin.left},0)`)
      .call((g) => g.select('.domain').remove());

    svg.append('g').selectAll('g').data(series).join('g')
      .style('fill', (s) => STATUS_COLORS[s.key])
      .selectAll('rect').data((s) => s.map((d) => ({ ...d, key: s.key }))).join('rect')
      .attr('x', (d) => x(d.data.day)).attr('width', x.bandwidth())
      .attr('y', (d) => y(d[1])).attr('height', (d) => Math.max(0, y(d[0]) - y(d[1])))
      .attr('rx', 2)
      .on('mousemove', (event, d) => {
        const [px, py] = d3.pointer(event, wrap.current);
        setTip({ x: px + 14, y: py - 10, body: <><b>{d3.timeFormat('%a %d %b')(new Date(d.data.day))}</b>{keys.map((k) => <span key={k}><i style={{ background: STATUS_COLORS[k] }} />{STATUS_LABELS[k]}: {d.data[k]}</span>)}<span>Cost: ${d.data.costUsd.toFixed(4)}</span></> });
      })
      .on('mouseleave', () => setTip(null));

    // Thin the labels so they never overlap: at most ~10 across the width.
    const every = Math.max(1, Math.ceil(daily.length / Math.max(3, Math.floor(width / 80))));
    svg.append('g').attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).tickValues(x.domain().filter((_, i) => i % every === 0)).tickFormat((d) => d3.timeFormat('%d %b')(new Date(d))).tickSizeOuter(0));
    svg.append('g').attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(Math.min(5, yMax)).tickFormat(d3.format('~s')))
      .call((g) => g.select('.domain').remove());
  }, [width, daily]);

  return (
    <div className="chart" ref={wrap}>
      <Legend keys={keys} />
      <svg ref={svgRef} width="100%" height={height} />
      <Tooltip tip={tip} />
    </div>
  );
}

/** Outcome share for the selected range — a donut with the total inside. */
export function StatusDonut({ totals }) {
  const wrap = useRef(null); const svgRef = useRef(null);
  const width = useWidth(wrap);
  const [tip, setTip] = useState(null);
  const size = 220;

  useEffect(() => {
    if (!width) return;
    const svg = d3.select(svgRef.current).attr('viewBox', [0, 0, size, size]);
    svg.selectAll('*').remove();
    const data = ['done', 'rendering', 'error', 'resolved'].map((key) => ({ key, value: totals?.[key] ?? 0 })).filter((d) => d.value > 0);
    const radius = size / 2;
    const g = svg.append('g').attr('transform', `translate(${radius},${radius})`);
    const arc = d3.arc().innerRadius(radius - 34).outerRadius(radius - 6).cornerRadius(3).padAngle(0.02);
    const pie = d3.pie().value((d) => d.value).sort(null);

    if (!data.length) {
      g.append('circle').attr('r', radius - 20).attr('fill', 'none').style('stroke', 'var(--chart-track)').attr('stroke-width', 26);
    }
    g.selectAll('path').data(pie(data)).join('path')
      .attr('d', arc).style('fill', (d) => STATUS_COLORS[d.data.key])
      .on('mousemove', (event, d) => {
        const [px, py] = d3.pointer(event, wrap.current);
        const pct = totals.total ? Math.round((d.data.value / totals.total) * 100) : 0;
        setTip({ x: px + 14, y: py - 10, body: <><b>{STATUS_LABELS[d.data.key]}</b><span>{d.data.value} renders · {pct}%</span></> });
      })
      .on('mouseleave', () => setTip(null));
    g.append('text').attr('text-anchor', 'middle').attr('dy', '-0.1em').attr('class', 'donut-total').text(totals?.total ?? 0);
    g.append('text').attr('text-anchor', 'middle').attr('dy', '1.5em').attr('class', 'donut-label').text('renders');
  }, [width, totals]);

  return (
    <div className="chart donut" ref={wrap}>
      <svg ref={svgRef} width={size} height={size} />
      <Legend keys={['done', 'rendering', 'error']} />
      <Tooltip tip={tip} />
    </div>
  );
}

/** Estimated render spend per day as an area line. */
export function CostByDay({ daily }) {
  const wrap = useRef(null); const svgRef = useRef(null);
  const width = useWidth(wrap);
  const [tip, setTip] = useState(null);
  const height = 200;

  useEffect(() => {
    if (!width || !daily?.length) return;
    const margin = { top: 12, right: 12, bottom: 34, left: 52 };
    const svg = d3.select(svgRef.current).attr('viewBox', [0, 0, width, height]);
    svg.selectAll('*').remove();
    const points = daily.map((d) => ({ ...d, date: new Date(d.day) }));
    const x = d3.scaleTime().domain(d3.extent(points, (d) => d.date)).range([margin.left, width - margin.right]);
    const y = d3.scaleLinear().domain([0, d3.max(points, (d) => d.costUsd) || 0.01]).nice().range([height - margin.bottom, margin.top]);

    svg.append('g').attr('class', 'grid').attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(4).tickSize(-(width - margin.left - margin.right)).tickFormat(''))
      .call((g) => g.select('.domain').remove());
    svg.append('path').datum(points).style('fill', 'var(--chart-area)')
      .attr('d', d3.area().x((d) => x(d.date)).y0(y(0)).y1((d) => y(d.costUsd)).curve(d3.curveMonotoneX));
    svg.append('path').datum(points).attr('fill', 'none').style('stroke', 'var(--chart-accent)').attr('stroke-width', 1.5)
      .attr('d', d3.line().x((d) => x(d.date)).y((d) => y(d.costUsd)).curve(d3.curveMonotoneX));
    svg.append('g').attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).ticks(Math.max(2, Math.floor(width / 110))).tickFormat(d3.timeFormat('%d %b')).tickSizeOuter(0));
    svg.append('g').attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(4).tickFormat((v) => `$${d3.format('.2~f')(v)}`))
      .call((g) => g.select('.domain').remove());

    // One invisible hit band per point, so hovering anywhere near a day
    // shows that day's figure rather than needing to land on the line.
    const bisect = d3.bisector((d) => d.date).center;
    const focus = svg.append('circle').attr('r', 3.5).style('fill', 'var(--chart-accent)').style('stroke', 'var(--surface)').attr('stroke-width', 2).style('display', 'none');
    svg.append('rect').attr('fill', 'transparent').attr('x', margin.left).attr('y', margin.top)
      .attr('width', width - margin.left - margin.right).attr('height', height - margin.top - margin.bottom)
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const d = points[bisect(points, x.invert(mx))];
        if (!d) return;
        focus.style('display', null).attr('cx', x(d.date)).attr('cy', y(d.costUsd));
        const [px, py] = d3.pointer(event, wrap.current);
        setTip({ x: px + 14, y: py - 10, body: <><b>{d3.timeFormat('%a %d %b')(d.date)}</b><span>${d.costUsd.toFixed(4)} estimated</span><span>{d.done + d.error + d.rendering} renders</span></> });
      })
      .on('mouseleave', () => { focus.style('display', 'none'); setTip(null); });
  }, [width, daily]);

  return (
    <div className="chart" ref={wrap}>
      <svg ref={svgRef} width="100%" height={height} />
      <Tooltip tip={tip} />
    </div>
  );
}

// --- customer analytics -------------------------------------------------------

/**
 * Categorical slots for "which one is this" charts (countries). The order is
 * the colour-blindness safety mechanism, not decoration: adjacent slots were
 * validated to stay distinct under protan/deutan/tritan vision. Anything past
 * the seventh slot folds into a neutral "Other" — never a generated hue.
 */
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];
export const OTHER_COLOR = 'var(--chart-other)';

/**
 * Share of a whole as a donut, with the whole in the middle and every slice
 * listed beside it with its count and share — the list is the readable
 * version, the ring is the shape. `slices` is [{ key, label, title, value, color }]
 * with `label` any React node, so callers may put a flag in it.
 */
export function ShareDonut({ slices, total, unit = 'customers' }) {
  const wrap = useRef(null); const svgRef = useRef(null);
  const width = useWidth(wrap);
  const [tip, setTip] = useState(null);
  const [active, setActive] = useState(null);
  const size = 200;
  const sum = total ?? d3.sum(slices, (d) => d.value);

  useEffect(() => {
    if (!width) return;
    const svg = d3.select(svgRef.current).attr('viewBox', [0, 0, size, size]);
    svg.selectAll('*').remove();
    const data = slices.filter((d) => d.value > 0);
    const radius = size / 2;
    const g = svg.append('g').attr('transform', `translate(${radius},${radius})`);
    const arc = d3.arc().innerRadius(radius - 30).outerRadius(radius - 6).cornerRadius(3).padAngle(0.02);
    const pie = d3.pie().value((d) => d.value).sort(null);

    if (!data.length) {
      g.append('circle').attr('r', radius - 18).attr('fill', 'none').style('stroke', 'var(--chart-track)').attr('stroke-width', 24);
    }
    g.selectAll('path').data(pie(data)).join('path')
      .attr('d', arc).style('fill', (d) => d.data.color)
      .style('stroke', 'var(--surface)').attr('stroke-width', 2)
      .attr('opacity', (d) => (active && active !== d.data.key ? 0.35 : 1))
      .on('mousemove', (event, d) => {
        const [px, py] = d3.pointer(event, wrap.current);
        setActive(d.data.key);
        setTip({ x: px + 14, y: py - 10, body: <><b>{d.data.title ?? d.data.key}</b><span>{d.data.value} {unit} · {sum ? Math.round((d.data.value / sum) * 100) : 0}%</span></> });
      })
      .on('mouseleave', () => { setActive(null); setTip(null); });
    g.append('text').attr('text-anchor', 'middle').attr('dy', '-0.1em').attr('class', 'donut-total').text(sum);
    g.append('text').attr('text-anchor', 'middle').attr('dy', '1.5em').attr('class', 'donut-label').text(unit);
  }, [width, slices, sum, unit, active]);

  return (
    <div className="chart share-donut" ref={wrap}>
      <svg ref={svgRef} width={size} height={size} />
      <ul className="share-list">
        {slices.map((d) => (
          <li key={d.key} className={active && active !== d.key ? 'is-dim' : ''} onMouseEnter={() => setActive(d.key)} onMouseLeave={() => setActive(null)}>
            <i style={{ background: d.color }} /><span className="share-label">{d.label}</span>
            <b>{d.value}</b><small>{sum ? Math.round((d.value / sum) * 100) : 0}%</small>
          </li>
        ))}
      </ul>
      <Tooltip tip={tip} />
    </div>
  );
}

/**
 * One bar a day, single series, so no legend. `value` names the field drawn;
 * `tooltip(d)` returns the lines under the date, so each page words its own.
 */
export function CountByDay({ rows, value, tooltip, color = 'var(--chart-accent)', format = '~s' }) {
  const wrap = useRef(null); const svgRef = useRef(null);
  const width = useWidth(wrap);
  const [tip, setTip] = useState(null);
  // Read through a ref: callers pass an inline function, and redrawing the
  // chart on every hover because its identity changed would drop the hover.
  const tooltipRef = useRef(tooltip);
  tooltipRef.current = tooltip;
  const height = 220;

  useEffect(() => {
    if (!width || !rows?.length) return;
    const margin = { top: 12, right: 8, bottom: 34, left: 36 };
    const svg = d3.select(svgRef.current).attr('viewBox', [0, 0, width, height]);
    svg.selectAll('*').remove();

    const x = d3.scaleBand().domain(rows.map((d) => d.day)).range([margin.left, width - margin.right]).padding(0.3);
    const yMax = d3.max(rows, (d) => d[value]) || 1;
    const y = d3.scaleLinear().domain([0, yMax]).nice().range([height - margin.bottom, margin.top]);

    svg.append('g').attr('class', 'grid')
      .call(d3.axisLeft(y).ticks(Math.min(5, yMax)).tickSize(-(width - margin.left - margin.right)).tickFormat(''))
      .attr('transform', `translate(${margin.left},0)`)
      .call((g) => g.select('.domain').remove());

    svg.append('g').style('fill', color).selectAll('rect').data(rows).join('rect')
      .attr('x', (d) => x(d.day)).attr('width', x.bandwidth())
      .attr('y', (d) => y(d[value])).attr('height', (d) => Math.max(0, y(0) - y(d[value])))
      .attr('rx', 2);
    // A full-height hit band per day, so a zero-height bar is still hoverable.
    svg.append('g').selectAll('rect').data(rows).join('rect')
      .attr('fill', 'transparent').attr('x', (d) => x(d.day) - (x.step() - x.bandwidth()) / 2).attr('width', x.step())
      .attr('y', margin.top).attr('height', height - margin.top - margin.bottom)
      .on('mousemove', (event, d) => {
        const [px, py] = d3.pointer(event, wrap.current);
        setTip({ x: px + 14, y: py - 10, body: <><b>{d3.timeFormat('%a %d %b')(new Date(d.day))}</b>{tooltipRef.current(d)}</> });
      })
      .on('mouseleave', () => setTip(null));

    const every = Math.max(1, Math.ceil(rows.length / Math.max(3, Math.floor(width / 80))));
    svg.append('g').attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).tickValues(x.domain().filter((_, i) => i % every === 0)).tickFormat((d) => d3.timeFormat('%d %b')(new Date(d))).tickSizeOuter(0));
    svg.append('g').attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(Math.min(5, Math.ceil(yMax))).tickFormat(d3.format(format)))
      .call((g) => g.select('.domain').remove());
  }, [width, rows, value, color, format]);

  return (
    <div className="chart" ref={wrap}>
      <svg ref={svgRef} width="100%" height={height} />
      <Tooltip tip={tip} />
    </div>
  );
}

/**
 * Bars per day stacked by `series` ([{ key, label, color }], bottom first),
 * with a legend. Like RendersByDay, but the caller names the series, so any
 * page can stack its own outcomes.
 */
export function StackByDay({ rows, series, tooltip }) {
  const wrap = useRef(null); const svgRef = useRef(null);
  const width = useWidth(wrap);
  const [tip, setTip] = useState(null);
  const tooltipRef = useRef(tooltip);
  tooltipRef.current = tooltip;
  const height = 240;

  useEffect(() => {
    if (!width || !rows?.length) return;
    const margin = { top: 12, right: 8, bottom: 34, left: 36 };
    const svg = d3.select(svgRef.current).attr('viewBox', [0, 0, width, height]);
    svg.selectAll('*').remove();
    const keys = series.map((s) => s.key);
    const colorOf = Object.fromEntries(series.map((s) => [s.key, s.color]));

    const x = d3.scaleBand().domain(rows.map((d) => d.day)).range([margin.left, width - margin.right]).padding(rows.length > 40 ? 0.15 : 0.3);
    const yMax = d3.max(rows, (d) => keys.reduce((sum, k) => sum + (d[k] ?? 0), 0)) || 1;
    const y = d3.scaleLinear().domain([0, yMax]).nice().range([height - margin.bottom, margin.top]);

    svg.append('g').attr('class', 'grid')
      .call(d3.axisLeft(y).ticks(Math.min(5, yMax)).tickSize(-(width - margin.left - margin.right)).tickFormat(''))
      .attr('transform', `translate(${margin.left},0)`)
      .call((g) => g.select('.domain').remove());

    svg.append('g').selectAll('g').data(d3.stack().keys(keys)(rows)).join('g')
      .style('fill', (s) => colorOf[s.key])
      .selectAll('rect').data((s) => s).join('rect')
      .attr('x', (d) => x(d.data.day)).attr('width', x.bandwidth())
      .attr('y', (d) => y(d[1])).attr('height', (d) => Math.max(0, y(d[0]) - y(d[1])))
      .attr('rx', 2);
    svg.append('g').selectAll('rect').data(rows).join('rect')
      .attr('fill', 'transparent').attr('x', (d) => x(d.day) - (x.step() - x.bandwidth()) / 2).attr('width', x.step())
      .attr('y', margin.top).attr('height', height - margin.top - margin.bottom)
      .on('mousemove', (event, d) => {
        const [px, py] = d3.pointer(event, wrap.current);
        setTip({ x: px + 14, y: py - 10, body: <><b>{d3.timeFormat('%a %d %b')(new Date(d.day))}</b>{tooltipRef.current(d)}</> });
      })
      .on('mouseleave', () => setTip(null));

    const every = Math.max(1, Math.ceil(rows.length / Math.max(3, Math.floor(width / 80))));
    svg.append('g').attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).tickValues(x.domain().filter((_, i) => i % every === 0)).tickFormat((d) => d3.timeFormat('%d %b')(new Date(d))).tickSizeOuter(0));
    svg.append('g').attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(Math.min(5, yMax)).tickFormat(d3.format('~s')))
      .call((g) => g.select('.domain').remove());
  }, [width, rows, series]);

  return (
    <div className="chart" ref={wrap}>
      <div className="chart-legend">{series.map((s) => <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>)}</div>
      <svg ref={svgRef} width="100%" height={height} />
      <Tooltip tip={tip} />
    </div>
  );
}

/** New accounts per day. */
export const SignupsByDay = ({ signups }) => (
  <CountByDay rows={signups} value="users" tooltip={(d) => <span>{d.users} new customer{d.users === 1 ? '' : 's'}</span>} />
);

/**
 * Ordered buckets as horizontal bars, count written at the end of each bar so
 * the exact figure never depends on reading an axis. `rows` is
 * [{ key, label, title, value }] already in display order; one hue throughout —
 * the bars differ by position, not identity.
 */
export function BucketBars({ rows, color = 'var(--chart-accent)', unit = 'customers' }) {
  const wrap = useRef(null); const svgRef = useRef(null);
  const width = useWidth(wrap);
  const [tip, setTip] = useState(null);
  const rowHeight = 30;
  const margin = { top: 6, right: 44, bottom: 6, left: 88 };
  const height = margin.top + margin.bottom + rows.length * rowHeight;
  const total = d3.sum(rows, (d) => d.value);

  useEffect(() => {
    if (!width || !rows.length) return;
    const svg = d3.select(svgRef.current).attr('viewBox', [0, 0, width, height]);
    svg.selectAll('*').remove();
    const y = d3.scaleBand().domain(rows.map((d) => d.key)).range([margin.top, height - margin.bottom]).padding(0.35);
    const x = d3.scaleLinear().domain([0, d3.max(rows, (d) => d.value) || 1]).range([margin.left, width - margin.right]);

    svg.append('g').selectAll('rect').data(rows).join('rect')
      .style('fill', 'var(--chart-track)').attr('x', margin.left).attr('y', (d) => y(d.key)).attr('width', width - margin.left - margin.right).attr('height', y.bandwidth()).attr('rx', 3);
    svg.append('g').selectAll('rect').data(rows).join('rect')
      .style('fill', color).attr('x', margin.left).attr('y', (d) => y(d.key))
      .attr('width', (d) => Math.max(d.value > 0 ? 3 : 0, x(d.value) - margin.left)).attr('height', y.bandwidth()).attr('rx', 3);
    svg.append('g').selectAll('text').data(rows).join('text')
      .attr('class', 'bar-label').attr('x', margin.left - 10).attr('y', (d) => y(d.key) + y.bandwidth() / 2).attr('dy', '0.35em').attr('text-anchor', 'end').text((d) => d.label);
    svg.append('g').selectAll('text').data(rows).join('text')
      .attr('class', 'bar-value').attr('x', (d) => x(d.value) + 8).attr('y', (d) => y(d.key) + y.bandwidth() / 2).attr('dy', '0.35em').text((d) => d.value);
    svg.append('g').selectAll('rect').data(rows).join('rect')
      .attr('fill', 'transparent').attr('x', 0).attr('y', (d) => y(d.key) - (y.step() - y.bandwidth()) / 2).attr('width', width).attr('height', y.step())
      .on('mousemove', (event, d) => {
        const [px, py] = d3.pointer(event, wrap.current);
        setTip({ x: px + 14, y: py - 10, body: <><b>{d.title ?? d.label}</b><span>{d.value} {unit} · {total ? Math.round((d.value / total) * 100) : 0}%</span></> });
      })
      .on('mouseleave', () => setTip(null));
  }, [width, rows, color, unit, total, height]);

  return (
    <div className="chart" ref={wrap}>
      <svg ref={svgRef} width="100%" height={height} />
      <Tooltip tip={tip} />
    </div>
  );
}
